// Real-time Data Service — v2 schema
// One Firestore subscription per machine doc. Drivers write machines/{id}.latest
// directly; no separate energy_readings / vibration_history / machine_history
// collections are read. No fake watts, no synthetic timestamps.

import { collection, doc, onSnapshot, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Map legacy monitorType values onto the new monitorKind enum so docs that
// still carry the old field render correctly (eventually they decay to OFFLINE
// via the staleness rule and become irrelevant).
const LEGACY_KIND_MAP = {
  bsr_monitor: 'analog',
  iotawatt:    'analog',
  digital_io:  'digital',
  focas:       'cnc',
};

export const resolveMonitorKind = (m) =>
  m?.monitorKind || LEGACY_KIND_MAP[m?.monitorType] || 'unknown';

// Normalize a Firestore Timestamp / number / Date to ms, or null if absent.
const toMs = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (v?.toMillis) return v.toMillis();
  if (v instanceof Date) return v.getTime();
  return null;
};

// Strip Firestore Timestamps out of latest{} so consumers get plain ms numbers.
const normalizeLatest = (latest) => {
  if (!latest) return null;
  return { ...latest, timestamp: toMs(latest.timestamp) };
};

class RealtimeDataService {
  constructor() {
    this.subscriptions = new Map();
  }

  // Subscribe to a single machine doc.
  // Callback receives the normalized doc on every push (~30s for BSR boards).
  subscribeMachineState(machineId, callback) {
    const docRef = doc(db, 'machines', machineId);

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) { callback(null); return; }
        const data = { id: snap.id, ...snap.data() };
        data.latest = normalizeLatest(data.latest);
        data.timestamp = data.latest?.timestamp ?? null;
        data.monitorKind = resolveMonitorKind(data);
        callback(data);
      },
      (error) => {
        console.error(`subscribeMachineState(${machineId}) error:`, error);
        callback(null, error);
      },
    );

    this.subscriptions.set(`machine_${machineId}`, unsubscribe);
    return unsubscribe;
  }

  // Subscribe to all machines for the fleet dashboard.
  subscribeAllMachines(callback) {
    const machinesRef = collection(db, 'machines');

    const unsubscribe = onSnapshot(
      machinesRef,
      (snap) => {
        const machines = [];
        snap.forEach((d) => {
          const data = { id: d.id, ...d.data() };
          data.latest = normalizeLatest(data.latest);
          data.timestamp = data.latest?.timestamp ?? null;
          data.monitorKind = resolveMonitorKind(data);
          machines.push(data);
        });
        callback(machines, null);
      },
      (error) => {
        console.error('subscribeAllMachines error:', error);
        callback(null, error);
      },
    );

    this.subscriptions.set('all_machines', unsubscribe);
    return unsubscribe;
  }

  unsubscribeAll() {
    this.subscriptions.forEach((unsub) => {
      try { unsub(); } catch (e) { /* noop */ }
    });
    this.subscriptions.clear();
  }

  // Time-series for one machine. readings/{autoId} carries machineId + timestamp
  // (real Firestore Timestamp) plus the same chNN_amps + totalAmps fields as latest{}.
  // Composite index required: readings (machineId ASC, timestamp ASC).
  async getReadingsHistory(machineId, hours = 24) {
    if (!db) return [];
    const cutoff = Timestamp.fromMillis(Date.now() - hours * 3600000);
    const q = query(
      collection(db, 'readings'),
      where('machineId', '==', machineId),
      where('timestamp', '>=', cutoff),
      orderBy('timestamp', 'asc'),
    );
    try {
      const snap = await getDocs(q);
      return snap.docs.map((d) => {
        const data = d.data();
        return { ...data, timestamp: data.timestamp?.toMillis?.() ?? null };
      }).filter((r) => r.timestamp != null);
    } catch (error) {
      console.error(`getReadingsHistory(${machineId}, ${hours}h) error:`, error);
      throw error;
    }
  }

  // Time-series across the whole fleet. No machineId filter — relies only on
  // the timestamp index, which is single-field and exists by default.
  async getFleetReadingsHistory(hours = 1) {
    if (!db) return [];
    const cutoff = Timestamp.fromMillis(Date.now() - hours * 3600000);
    const q = query(
      collection(db, 'readings'),
      where('timestamp', '>=', cutoff),
      orderBy('timestamp', 'asc'),
    );
    try {
      const snap = await getDocs(q);
      return snap.docs.map((d) => {
        const data = d.data();
        return { ...data, timestamp: data.timestamp?.toMillis?.() ?? null };
      }).filter((r) => r.timestamp != null);
    } catch (error) {
      console.error(`getFleetReadingsHistory(${hours}h) error:`, error);
      throw error;
    }
  }
}

const realtimeDataService = new RealtimeDataService();
export default realtimeDataService;
